import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type LookupPdpsByAsinV1OrganizationsOrgIdPdpsLookupByAsinPostError,
  type LookupPdpsByAsinV1OrganizationsOrgIdPdpsLookupByAsinPostResponse,
  lookupPdpsByAsinV1OrganizationsOrgIdPdpsLookupByAsinPost,
} from '../../generated';

export type LookupPdpsByAsinQuery = LookupPdpsByAsinV1OrganizationsOrgIdPdpsLookupByAsinPostResponse['results'];

export interface LookupPdpsByAsinQueryVariables {
  org_id: string;
  asins: string[];
}

const QUERY_KEY_BASE = 'LookupPdpsByAsin';

const MAX_ASINS_PER_REQUEST = 500;

const lookupKey = ({ org_id, asins }: LookupPdpsByAsinQueryVariables) =>
  [QUERY_KEY_BASE, org_id, [...asins].sort().join(',')] as const;

async function fetchLookupBatch(org_id: string, asins: string[]): Promise<LookupPdpsByAsinQuery> {
  const { data } = await lookupPdpsByAsinV1OrganizationsOrgIdPdpsLookupByAsinPost({
    path: { org_id },
    body: { asins },
    throwOnError: true,
  });

  return data.results;
}

async function fetchLookup({ org_id, asins }: LookupPdpsByAsinQueryVariables): Promise<LookupPdpsByAsinQuery> {
  const batches: string[][] = [];
  for (let start = 0; start < asins.length; start += MAX_ASINS_PER_REQUEST) {
    batches.push(asins.slice(start, start + MAX_ASINS_PER_REQUEST));
  }

  const results = await Promise.all(batches.map((batch) => fetchLookupBatch(org_id, batch)));

  return Object.assign({}, ...results);
}

export const useLookupPdpsByAsinQuery = <
  TData = LookupPdpsByAsinQuery,
  TError = LookupPdpsByAsinV1OrganizationsOrgIdPdpsLookupByAsinPostError,
>(
  variables: LookupPdpsByAsinQueryVariables,
  options?: Omit<UseQueryOptions<LookupPdpsByAsinQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<LookupPdpsByAsinQuery, TError, TData>({
    queryKey: lookupKey(variables),
    queryFn: () => fetchLookup(variables),
    enabled: Boolean(variables.org_id) && variables.asins.length > 0,
    ...options,
  });
};

useLookupPdpsByAsinQuery.getKey = () => [QUERY_KEY_BASE] as const;

useLookupPdpsByAsinQuery.fetcher = (variables: LookupPdpsByAsinQueryVariables) => () => fetchLookup(variables);
